import React, { useState, MouseEvent, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useAppDispatch } from 'store/hooks'
import { UserSearch, actions as adminActions } from 'store/adminArea'

import styles from './SingleSearchesTable.module.scss'

import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Box,
  CircularProgress,
  Checkbox,
  TablePagination,
  Link,
  Typography,
} from '@mui/material'
import { Search } from 'store/commonTypes'
import ResultTable from './resultTable/ResultTable'
import TruncatedText from './components/TruncatedText'
import GenerateMultisectorReport from './components/GenerateMultisectorReport'
import ExportMultipleResultsExcel from './components/ExportMultipleResultsExcel'
import { MdOutlineContentCopy as CopyIcon } from 'react-icons/md'
import CopyToClipboard from 'react-copy-to-clipboard'
import ExpandToggle from '../components/ExpandToggle'
import { getReportLinkText, hasReportAccess } from 'containers/tables/searchStatusHelpers'

type Props = {
  userId: string
}

interface Column {
  id: string
  label: string
  minWidth?: number
  maxwidth?: number
  wordBreak?: string
  align?: 'right'
  format?: (row: Search) => React.ReactNode
}

function SearchesList({ userId }: Props) {
  const dispatch = useAppDispatch()
  const [clipboard, setClipboard] = useState(false)
  const searchesPerPage = useSelector((state: RootState) => state.adminArea.userDetails).searches
  const totalSearchesCount = useSelector((state: RootState) => state.adminArea.userDetails).totalSearchesCount
  const multisectorId = useSelector((state: RootState) => state.adminArea.multisectorId)
  const isLoading = useSelector((state: RootState) => state.adminArea.loading.searches)
  const error = useSelector((state: RootState) => state.adminArea.error.searches)

  const [expandedSearchId, setExpandedSearchId] = useState('')
  const [searchesSelected, setSearchesSelected] = useState<string[]>([])
  const [multisectorIsSubmitted, setMultisectorIsSubmitted] = useState<boolean>(false)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(30)

  useEffect(() => {
    //TODO getUserSearches should go in dashboard store
    dispatch(adminActions.getUserSearches({ userId, limit: rowsPerPage, page }))
  }, [dispatch, userId, page, rowsPerPage])

  useEffect(() => {
    if (multisectorIsSubmitted && multisectorId) {
      window.open(`/dashboard/reporting/company/${multisectorId}`, '_blank')
    }
  }, [multisectorId])

  const handleGetResults = (searchId: string) => {
    const data: UserSearch = { userId: userId, searchId: searchId }
    dispatch(adminActions.getUserSearchResults(data))
  }

  const selectSearchesBetweenTwoIds = (selectedSearchId: string) => {
    const lastSearchId = searchesSelected.slice(-1)[0]
    const filteredSearches = searchesPerPage!.filter(search => !['error', 'queued', 'search_processing'].includes(search.status))
    const allSearchIds = filteredSearches!.map(search => search.ID)
    const index1 = allSearchIds.indexOf(lastSearchId)
    const index2 = allSearchIds.indexOf(selectedSearchId)

    // If one or both are not in the list we just add it
    if (index1 === -1 || index2 === -1) {
      setSearchesSelected([...searchesSelected, selectedSearchId])
    } else {
      // slice to get the elements between index1 and index2 (ambos inclusive)
      const elementosEntreIds = allSearchIds.slice(Math.min(index1, index2), Math.max(index1, index2) + 1)
      const conjuntoIds = new Set(searchesSelected)
      elementosEntreIds.forEach(id => conjuntoIds.add(id))
      const newIdsSelected = Array.from(conjuntoIds)
      setSearchesSelected(newIdsSelected)
    }
  }

  const handleCheckboxChange = (event: MouseEvent<HTMLButtonElement>, searchId: string): void => {
    if (searchesSelected.includes(searchId)) {
      setSearchesSelected(searchesSelected.filter(selected => selected !== searchId))
    } else {
      if (event.shiftKey) {
        selectSearchesBetweenTwoIds(searchId)
      } else setSearchesSelected([...searchesSelected, searchId])
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const getDate = (timestamp: string) => {
    if (timestamp) {
      const d = new Date(timestamp)
      const date = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} `
      const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : d.getMinutes()
      const seconds = d.getSeconds() < 10 ? `0${d.getSeconds()}` : d.getSeconds()
      const time = `${d.getHours()}:${minutes}:${seconds} `
      return date + time
    } else return ''
  }

  const columns: Column[] = [
    {
      id: 'ID',
      label: '#',
      maxwidth: 20,
      format: (row: Search) => (
        <Checkbox
          size="small"
          checked={searchesSelected.includes(row.ID)}
          onClick={event => handleCheckboxChange(event, row.ID)}
        />
      ),
    },
    {
      id: 'ID',
      label: '#',
      maxwidth: 15,
      format: (
        row, // Show expand toggle only if search has finished
      ) =>
        hasReportAccess(row.status) ? (
          <ExpandToggle
            expanded={expandedSearchId === row.ID}
            onToggle={() => {
              setExpandedSearchId(expandedSearchId === row.ID ? '' : row.ID)
              handleGetResults(row.ID)
            }}
          />
        ) : (
          ''
        ),
    },
    { id: 'name', label: 'Name', wordBreak: 'break-all', maxwidth: 300 },
    {
      id: 'status',
      label: 'Search status',
      format: row => {
        return row.status
      },
    },
    { id: 'created_at', label: 'Created at', maxwidth: 90, format: row => getDate(row.created_at) },

    { id: 'patent_number', label: 'Patent Num' },
    { id: 'ipcr_subgroup', label: 'Subgroup', maxwidth: 120 },
    {
      id: 'ID',
      label: 'Report',
      format: row => {
        const linkText = getReportLinkText(row.status)

        if (!linkText) return '—'

        return (
          <Link href={`/dashboard/reporting/single-search/${row.ID}`} target="_blank">
            {linkText}
          </Link>
        )
      },
    },
  ]

  return (
    <div className={styles.base}>
      <Box sx={{ textAlign: 'center', width: 1 }}>
        {isLoading && <CircularProgress />}
        {error && (
          <Typography p={1} color={'red'}>
            {error}
          </Typography>
        )}
      </Box>
      {searchesPerPage?.length && (
        <>
          <Box display="flex" flexDirection={'column'} mb={1}>
            <Box sx={{ mr: 1 }} className={styles.optContainer}>
              <ExportMultipleResultsExcel userId={userId} searchesSelected={searchesSelected} />
            </Box>

            <Box flexGrow={1} className={styles.optContainer}>
              <GenerateMultisectorReport
                userId={userId}
                searchesSelected={searchesSelected}
                handleMultisectorSubmitted={() => setMultisectorIsSubmitted(true)}
              />
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 950 }} stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map(column => (
                    <TableCell
                      key={column.id + column.label}
                      align={column.align}
                      sx={{ paddingLeft: 1 }}
                      style={{ maxWidth: column.maxwidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {searchesPerPage.slice(0, rowsPerPage).map(search => (
                  <React.Fragment key={search.ID}>
                    <TableRow hover role="checkbox" tabIndex={-1} key={search.ID}>
                      {columns.map(col => (
                        <TableCell key={col.id}>{col.format ? col.format(search) : (search as any)[col.id]}</TableCell>
                      ))}
                    </TableRow>
                    {expandedSearchId === search.ID && (
                      <TableRow>
                        <TableCell sx={{ p: 0, border: 0 }} colSpan={columns.length}>
                          {['error', 'search_processing', 'queued'].includes(search.status) ? (
                            <Box p={2}>Status {search.status}</Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                fontSize: '13px',
                                padding: '20px',
                                backgroundColor: 'rgb(238, 238, 238)',
                                boxShadow: 'inset 0 0 5px 0px rgb(139 139 139 / 10%)',
                              }}>
                              <Box mb={2} display={'flex'}>
                                <strong>ID: </strong>
                                <Box component={'span'} mx={1}>
                                  {search.ID}
                                </Box>
                                <CopyToClipboard
                                  text={search.ID}
                                  onCopy={() => {
                                    setClipboard(true)
                                    setTimeout(() => {
                                      setClipboard(false)
                                    }, 2000)
                                  }}>
                                  <Box
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover svg': { color: 'primary.main' },
                                    }}>
                                    <CopyIcon size="1.1rem" />
                                  </Box>
                                </CopyToClipboard>
                                {clipboard && <div>Copied!</div>}
                              </Box>
                              <Box mb={2}>
                                <strong>Categories:</strong> {search.filter.classificationsIPCR?.join(', ')}
                              </Box>
                              <Box mb={2}>
                                <strong>Territories:</strong> {search.filter.countries?.join(', ')}
                              </Box>
                              <Box mb={2}>
                                <strong>Text:</strong>{' '}
                                <TruncatedText title={search.text} lines={1}>
                                  {search.text}
                                </TruncatedText>
                              </Box>
                              <Box>
                                <strong>Terms:</strong>{' '}
                                <TruncatedText title={search.terms.join(', ')} lines={1}>
                                  {search.terms.join(', ')}
                                </TruncatedText>
                              </Box>

                              <ResultTable userId={userId} search={search} results={search?.results} />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            {searchesPerPage && searchesPerPage?.length === 0 && <Stack p={2}>No searches created</Stack>}
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[30, 60, 100]}
            component="div"
            count={totalSearchesCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </div>
  )
}

export default SearchesList
